import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Search, X, Newspaper, Calendar, Building2, Loader2 } from "lucide-react";
import { useLang } from "@/context/LangContext";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;

export default function UniversalSearch() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState({ companies: [], events: [], news: [] });
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const nav = useNavigate();

  // Debounced fetch
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!open || q.trim().length < 2) {
      setResults({ companies: [], events: [], news: [] });
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(() => {
      api.get("/search", { params: { q } })
        .then((r) => setResults(r.data))
        .finally(() => setLoading(false));
    }, 250);
  }, [q, open]);

  // ESC to close + Cmd/Ctrl+K to open
  useEffect(() => {
    const handler = (e) => {
      if (open && e.key === "Escape") setOpen(false);
      if (!open && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  const total = results.companies.length + results.events.length + results.news.length;
  const go = (path) => { setOpen(false); setQ(""); nav(path); };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-medium text-neutral-600 hover:border-wine-300 hover:text-wine-700"
        aria-label={t("search_button")}
        data-testid="universal-search-open"
      >
        <Search className="h-3.5 w-3.5" />
        <span className="hidden sm:inline">{t("search_button")}</span>
        <kbd className="hidden rounded bg-neutral-100 px-1.5 py-0.5 font-mono text-[10px] text-neutral-500 sm:inline">⌘K</kbd>
      </button>

      {open && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center pt-16" data-testid="universal-search-modal">
          <button onClick={() => setOpen(false)} className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" aria-label="Fermer" />
          <div className="relative w-full max-w-2xl rounded-3xl bg-white shadow-2xl">
            <div className="flex items-center gap-3 border-b border-neutral-100 px-5 py-4">
              <Search className="h-5 w-5 text-neutral-400" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("search_placeholder")}
                className="flex-1 bg-transparent text-base outline-none placeholder:text-neutral-400"
                data-testid="universal-search-input"
              />
              {loading && <Loader2 className="h-4 w-4 animate-spin text-wine-500" />}
              <button onClick={() => setOpen(false)} className="grid h-7 w-7 place-items-center rounded-full text-neutral-400 hover:bg-neutral-100" data-testid="universal-search-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="max-h-[65vh] overflow-y-auto px-3 py-3" data-testid="universal-search-results">
              {q.trim().length < 2 && (
                <div className="py-10 text-center text-sm text-neutral-400">Tapez au moins 2 caractères pour rechercher</div>
              )}
              {q.trim().length >= 2 && !loading && total === 0 && (
                <div className="py-10 text-center text-sm text-neutral-400" data-testid="universal-search-empty">Aucun résultat pour <span className="font-semibold text-neutral-700">"{q}"</span></div>
              )}
              {results.companies.length > 0 && (
                <Section title="Membres" icon={Building2}>
                  {results.companies.map((c) => (
                    <button key={c.id} onClick={() => go(`/members/${c.slug}`)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-wine-50" data-testid={`search-company-${c.slug}`}>
                      {c.logo ? <img src={resolveUrl(c.logo)} alt="" className="h-9 w-9 rounded-lg object-contain bg-white p-0.5" /> : <div className="h-9 w-9 rounded-lg bg-wine-100" />}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-neutral-900">{c.name}</div>
                        <div className="truncate text-xs text-neutral-500">{c.tagline || `${c.city}, ${c.country}`}</div>
                      </div>
                      {c.main_pole && <span className="rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-semibold text-wine-700">{c.main_pole}</span>}
                    </button>
                  ))}
                </Section>
              )}
              {results.events.length > 0 && (
                <Section title="Événements" icon={Calendar}>
                  {results.events.map((ev) => (
                    <button key={ev.id} onClick={() => go(`/events/${ev.slug}`)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-wine-50" data-testid={`search-event-${ev.slug}`}>
                      {ev.image ? <img src={resolveUrl(ev.image)} alt="" className="h-9 w-12 rounded-lg object-cover" /> : <div className="h-9 w-12 rounded-lg bg-wine-100" />}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-neutral-900">{ev.title}</div>
                        <div className="truncate text-xs text-neutral-500">{new Date(ev.date).toLocaleDateString("fr")} · {ev.location}</div>
                      </div>
                      <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] text-neutral-600">{ev.format}</span>
                    </button>
                  ))}
                </Section>
              )}
              {results.news.length > 0 && (
                <Section title="Actualités" icon={Newspaper}>
                  {results.news.map((n) => (
                    <button key={n.id} onClick={() => go(`/news/${n.slug}`)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left hover:bg-wine-50" data-testid={`search-news-${n.slug}`}>
                      {n.image ? <img src={resolveUrl(n.image)} alt="" className="h-9 w-12 rounded-lg object-cover" /> : <div className="h-9 w-12 rounded-lg bg-wine-100" />}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-semibold text-neutral-900">{n.title}</div>
                        <div className="truncate text-xs text-neutral-500">{n.excerpt}</div>
                      </div>
                      <span className="rounded-full bg-wine-50 px-2 py-0.5 text-[10px] font-semibold text-wine-700">{n.category}</span>
                    </button>
                  ))}
                </Section>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-neutral-100 bg-neutral-50/60 px-5 py-2.5 text-[11px] text-neutral-500">
              <span>{total > 0 ? `${total} résultat${total > 1 ? "s" : ""}` : ""}</span>
              <span><kbd className="rounded bg-white px-1.5 py-0.5 font-mono">Esc</kbd> pour fermer</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="mb-3">
      <div className="mb-1 flex items-center gap-1.5 px-2 text-[10px] font-bold uppercase tracking-wider text-neutral-500">
        <Icon className="h-3 w-3" /> {title}
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
